import { CylinderGeometry, Matrix3, Vector3 } from "three";
import type { DepthSnapshot } from "./depth-surface";

export type CylinderSurface = {
  center: Vector3;
  axis: Vector3;
  radial: Vector3;
  radius: number;
};

export const CYLINDER_DEVIATION_COLORS = {
  inside: "#3380ff",
  fitted: "#33d973",
  outside: "#ff4d33",
};

export const CYLINDER_RING_STYLE = {
  color: "#00d9ff",
  width: 1.25,
  opacity: 0.6,
};
const MIN_RING_SPACING = 0.25;
const MAX_RING_INTERVALS = 32;

const MIN_CIRCLE_CONDITION = 0.0001;
const MIN_RADIUS = 0.0025;
const MIN_AXIS_LENGTH_SQUARED = 0.9;

/**
 * @param points Surface samples in world coordinates.
 * @param direction Cylinder axis estimated from surface normals.
 * @returns Least-squares circular cross-section, or undefined for degenerate data.
 */
export function fitCylinderSection(
  points: Vector3[],
  direction: Vector3,
): CylinderSurface | undefined {
  if (points.length < 12 || direction.lengthSq() < MIN_AXIS_LENGTH_SQUARED) {
    return;
  }
  const axis = direction.clone().normalize();
  const radial = new Vector3(1, 0, 0).projectOnPlane(axis);
  if (radial.lengthSq() < 0.1) radial.set(0, 1, 0).projectOnPlane(axis);
  radial.normalize();
  const tangent = new Vector3().crossVectors(axis, radial);
  const mean = new Vector3();
  for (const point of points) mean.add(point);
  mean.divideScalar(points.length);
  const offset = new Vector3();
  let squareX = 0;
  let squareY = 0;
  let cross = 0;
  let targetX = 0;
  let targetY = 0;
  for (const point of points) {
    offset.subVectors(point, mean);
    const horizontal = offset.dot(radial);
    const vertical = offset.dot(tangent);
    const squared = horizontal ** 2 + vertical ** 2;
    squareX += horizontal ** 2;
    squareY += vertical ** 2;
    cross += horizontal * vertical;
    targetX += horizontal * squared * 0.5;
    targetY += vertical * squared * 0.5;
  }
  const determinant = squareX * squareY - cross ** 2;
  if (determinant <= MIN_CIRCLE_CONDITION * (squareX + squareY) ** 2) return;
  const coefficients = new Vector3(targetX, targetY, 0).applyMatrix3(
    new Matrix3().set(squareX, cross, 0, cross, squareY, 0, 0, 0, 1).invert(),
  );
  const radius = Math.sqrt(
    coefficients.lengthSq() + (squareX + squareY) / points.length,
  );
  if (!Number.isFinite(radius) || radius < MIN_RADIUS) return;
  return {
    center: mean
      .addScaledVector(radial, coefficients.x)
      .addScaledVector(tangent, coefficients.y),
    axis,
    radial,
    radius,
  };
}

/**
 * @param cylinder Fixed fitted cylinder.
 * @param point World position.
 * @param target Reusable output radial normal.
 * @returns Outward unit normal at the nearest cylindrical surface point.
 */
export function cylinderNormal(
  cylinder: CylinderSurface,
  point: Vector3,
  target = new Vector3(),
): Vector3 {
  target.subVectors(point, cylinder.center);
  return target
    .addScaledVector(cylinder.axis, -target.dot(cylinder.axis))
    .normalize();
}

/**
 * @param cylinder Fixed fitted cylinder.
 * @param point World position.
 * @returns Signed radial distance from the cylindrical surface.
 */
export function cylinderDistance(
  cylinder: CylinderSurface,
  point: Vector3,
): number {
  const horizontal = point.x - cylinder.center.x;
  const vertical = point.y - cylinder.center.y;
  const depth = point.z - cylinder.center.z;
  const axial =
    horizontal * cylinder.axis.x +
    vertical * cylinder.axis.y +
    depth * cylinder.axis.z;
  return (
    Math.sqrt(
      Math.max(0, horizontal ** 2 + vertical ** 2 + depth ** 2 - axial ** 2),
    ) - cylinder.radius
  );
}

/**
 * @param snapshot Unwrapped cylinder mask transform.
 * @param column Cell column, allowing fractional boundary coordinates.
 * @param row Cell row, allowing fractional boundary coordinates.
 * @param target Reusable world point.
 * @returns Point on the cylindrical surface.
 */
export function cylinderPosition(
  snapshot: DepthSnapshot,
  column: number,
  row: number,
  target: Vector3,
): Vector3 {
  const angle = ((column + 0.5) / snapshot.width - 0.5) * Math.PI * 2;
  return target
    .set(
      Math.cos(angle),
      Math.sin(angle),
      ((row + 0.5) / snapshot.height) * 2 - 1,
    )
    .applyMatrix4(snapshot.inverseViewProjection);
}

/**
 * @param snapshot Unwrapped cylinder mask transform.
 * @param mask Connected cylindrical surface cells.
 * @param origin Local overlay origin.
 * @returns Complete reconstruction over the observed axial extent, independent of coverage holes.
 */
export function cylinderGeometry(
  snapshot: DepthSnapshot,
  mask: Uint8Array,
  origin: Vector3,
): {
  positions: Float32Array;
  outline: Float32Array;
  endCaps: Float32Array;
  centerline: Float32Array;
  rings: Float32Array;
} {
  const { width, height } = snapshot;
  const outline: number[] = [];
  const point = new Vector3();
  if (!mask.some(Boolean)) {
    return {
      positions: new Float32Array(),
      outline: new Float32Array(),
      endCaps: new Float32Array(),
      centerline: new Float32Array(),
      rings: new Float32Array(),
    };
  }
  const geometry = new CylinderGeometry(1, 1, 2, Math.max(64, width), 1, false);
  const shellCount = geometry.groups[0].count * 3;
  const expanded = geometry.toNonIndexed();
  const localTransform = snapshot.inverseViewProjection.clone();
  localTransform.setPosition(
    new Vector3().setFromMatrixPosition(localTransform).sub(origin),
  );
  expanded.rotateX(Math.PI / 2).applyMatrix4(localTransform);
  const vertices = new Float32Array(expanded.getAttribute("position").array);
  geometry.dispose();
  expanded.dispose();
  function emit(column: number, row: number, target: number[]): void {
    cylinderPosition(snapshot, column - 0.5, row - 0.5, point).sub(origin);
    target.push(point.x, point.y, point.z);
  }
  for (let column = 0; column < width; column++) {
    for (const row of [0, height]) {
      emit(column, row, outline);
      emit(column + 1, row, outline);
    }
  }
  const centerline = [-1, 1].flatMap((end) =>
    point
      .set(0, 0, end)
      .applyMatrix4(snapshot.inverseViewProjection)
      .sub(origin)
      .toArray(),
  );
  const length =
    new Vector3()
      .setFromMatrixColumn(snapshot.inverseViewProjection, 2)
      .length() * 2;
  const diameter =
    new Vector3()
      .setFromMatrixColumn(snapshot.inverseViewProjection, 0)
      .length() * 2;
  const intervals = Math.min(
    MAX_RING_INTERVALS,
    Math.max(2, Math.ceil(length / Math.max(MIN_RING_SPACING, diameter))),
  );
  const rings: number[] = [];
  for (let ring = 1; ring < intervals; ring++) {
    const row = (height * ring) / intervals;
    for (let column = 0; column < width; column++) {
      emit(column, row, rings);
      emit(column + 1, row, rings);
    }
  }
  return {
    positions: vertices.slice(0, shellCount),
    outline: new Float32Array(outline),
    endCaps: vertices.slice(shellCount),
    centerline: new Float32Array(centerline),
    rings: new Float32Array(rings),
  };
}
